<div class="exercise-instructions-container">
  <!-- Header -->
  <div class="header-section">
    <div class="header-content">
      <button mat-icon-button class="back-button" (click)="goBack()">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <div class="header-text">
        <h1 class="page-title">Exercise Instructions</h1>
        <p class="member-name">{{ memberName }}</p>
      </div>
      <!-- Search Bar -->
      <div class="search-section">
        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Search exercises</mat-label>
          <input
            matInput
            type="text"
            [(ngModel)]="searchTerm"
            (input)="onSearchChange()"
            placeholder="Search by name, muscle group, difficulty, instructions, benefits..."
          />
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <mat-progress-spinner
      diameter="50"
      mode="indeterminate"
    ></mat-progress-spinner>
    <p>Loading exercise instructions...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error" class="error-container">
    <mat-icon class="error-icon">error</mat-icon>
    <p class="error-message">{{ error }}</p>
    <button mat-raised-button color="primary" (click)="loadMemberExercises()">
      Retry
    </button>
  </div>

  <!-- Exercise List -->
  <div *ngIf="!isLoading && !error" class="exercises-section">
    <!-- Empty State -->
    <div *ngIf="filteredExercises.length === 0" class="empty-state">
      <mat-icon class="empty-icon">fitness_center</mat-icon>
      <h3>No exercises found</h3>
      <p *ngIf="searchTerm">
        No exercises match your search criteria. Try adjusting your search
        terms.
      </p>
      <p *ngIf="!searchTerm">
        This member hasn't been assigned any exercises yet.
      </p>
    </div>

    <!-- Exercise Cards -->
    <div class="exercises-grid" *ngIf="filteredExercises.length > 0">
      <mat-card
        *ngFor="let exercise of filteredExercises; trackBy: trackByExerciseId"
        class="exercise-card"
        [class.has-instructions]="hasInstructions(exercise)"
      >
        <mat-card-header>
          <div mat-card-avatar class="exercise-avatar">
            <mat-icon>fitness_center</mat-icon>
          </div>
          <mat-card-title class="exercise-name">{{
            exercise.exerciseName
          }}</mat-card-title>
          <mat-card-subtitle>
            <div class="exercise-meta">
              <span
                class="difficulty-badge"
                [class]="
                  'difficulty-' +
                  (exercise.difficultyLevel?.toLowerCase() || 'beginner')
                "
              >
                {{ exercise.difficultyLevel || "BEGINNER" }}
              </span>
              <span class="muscle-group">{{
                exercise.muscleGroup || "General"
              }}</span>
              <span class="calories" *ngIf="exercise.estimatedCaloriesBurned">
                {{ exercise.estimatedCaloriesBurned }} cal
                {{ formatCalorieUnit(exercise.calorieUnit) }}
              </span>
            </div>
            <span
              class="assignment-info"
              *ngIf="exercise.totalAssignments && exercise.totalAssignments > 0"
            >
              Assigned {{ exercise.totalAssignments }} time(s)
            </span>
            <span class="last-assigned" *ngIf="exercise.lastAssignedDate">
              • Last: {{ formatDate(exercise.lastAssignedDate) }}
            </span>
          </mat-card-subtitle>
        </mat-card-header>

        <mat-card-content>
          <!-- Exercise Details -->
          <div class="exercise-details">
            <!-- Equipment Needed -->
            <div
              class="detail-section"
              *ngIf="
                exercise.equipmentNeeded && exercise.equipmentNeeded.length > 0
              "
            >
              <div class="detail-label-inline">
                <mat-icon>build</mat-icon>
                <span>Equipment:</span>
                <div class="equipment-list">
                  <span
                    *ngFor="let equipment of exercise.equipmentNeeded"
                    class="equipment-tag"
                  >
                    {{ formatEquipment(equipment) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Expandable Sections -->
            <div class="expandable-sections">
              <!-- Instructions -->
              <div
                class="expandable-section"
                *ngIf="
                  exercise.instructions && exercise.instructions.length > 0
                "
              >
                <button
                  mat-button
                  class="section-toggle"
                  (click)="toggleSection('instructions-' + exercise.exerciseId)"
                >
                  <mat-icon>list</mat-icon>
                  <span>Instructions ({{ exercise.instructions.length }})</span>
                  <mat-icon class="expand-icon">{{
                    isSectionExpanded("instructions-" + exercise.exerciseId)
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </button>
                <div
                  class="section-content"
                  *ngIf="
                    isSectionExpanded('instructions-' + exercise.exerciseId)
                  "
                >
                  <ol class="instruction-list">
                    <li *ngFor="let instruction of exercise.instructions">
                      {{ instruction }}
                    </li>
                  </ol>
                </div>
              </div>

              <!-- Benefits -->
              <div
                class="expandable-section"
                *ngIf="exercise.benefits && exercise.benefits.length > 0"
              >
                <button
                  mat-button
                  class="section-toggle"
                  (click)="toggleSection('benefits-' + exercise.exerciseId)"
                >
                  <mat-icon>star</mat-icon>
                  <span>Benefits ({{ exercise.benefits.length }})</span>
                  <mat-icon class="expand-icon">{{
                    isSectionExpanded("benefits-" + exercise.exerciseId)
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </button>
                <div
                  class="section-content"
                  *ngIf="isSectionExpanded('benefits-' + exercise.exerciseId)"
                >
                  <ul class="benefit-list">
                    <li *ngFor="let benefit of exercise.benefits">
                      {{ benefit }}
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Safety Tips -->
              <div
                class="expandable-section"
                *ngIf="exercise.safetyTips && exercise.safetyTips.length > 0"
              >
                <button
                  mat-button
                  class="section-toggle"
                  (click)="toggleSection('safety-' + exercise.exerciseId)"
                >
                  <mat-icon>security</mat-icon>
                  <span>Safety Tips ({{ exercise.safetyTips.length }})</span>
                  <mat-icon class="expand-icon">{{
                    isSectionExpanded("safety-" + exercise.exerciseId)
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </button>
                <div
                  class="section-content"
                  *ngIf="isSectionExpanded('safety-' + exercise.exerciseId)"
                >
                  <ul class="safety-list">
                    <li *ngFor="let tip of exercise.safetyTips">{{ tip }}</li>
                  </ul>
                </div>
              </div>

              <!-- Diabetic Guidance -->
              <div class="expandable-section" *ngIf="exercise.diabeticGuidance">
                <button
                  mat-button
                  class="section-toggle"
                  (click)="toggleSection('diabetic-' + exercise.exerciseId)"
                >
                  <mat-icon>medical_services</mat-icon>
                  <span>Diabetic Guidance</span>
                  <mat-icon class="expand-icon">{{
                    isSectionExpanded("diabetic-" + exercise.exerciseId)
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </button>
                <div
                  class="section-content"
                  *ngIf="isSectionExpanded('diabetic-' + exercise.exerciseId)"
                >
                  <p class="guidance-text">{{ exercise.diabeticGuidance }}</p>
                </div>
              </div>

              <!-- Risk Assessment -->
              <div class="expandable-section" *ngIf="exercise.riskAssessment">
                <button
                  mat-button
                  class="section-toggle"
                  (click)="toggleSection('risk-' + exercise.exerciseId)"
                >
                  <mat-icon>warning</mat-icon>
                  <span>Risk Assessment</span>
                  <mat-icon class="expand-icon">{{
                    isSectionExpanded("risk-" + exercise.exerciseId)
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </button>
                <div
                  class="section-content"
                  *ngIf="isSectionExpanded('risk-' + exercise.exerciseId)"
                >
                  <p class="risk-text">{{ exercise.riskAssessment }}</p>
                </div>
              </div>

              <!-- Custom Instructions Section -->
              <div class="expandable-section">
                <button
                  mat-button
                  class="section-toggle"
                  (click)="toggleSection('custom-' + exercise.exerciseId)"
                >
                  <mat-icon [class.has-content]="hasInstructions(exercise)">
                    {{
                      hasInstructions(exercise)
                        ? "description"
                        : "description_outlined"
                    }}
                  </mat-icon>
                  <span
                    >Custom Instructions{{
                      hasInstructions(exercise)
                        ? " (" +
                          getInstructionCount(exercise.customInstructions) +
                          ")"
                        : ""
                    }}</span
                  >
                  <mat-icon class="expand-icon">{{
                    isSectionExpanded("custom-" + exercise.exerciseId)
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </button>
                <div
                  class="section-content"
                  *ngIf="isSectionExpanded('custom-' + exercise.exerciseId)"
                >
                  <div
                    *ngIf="hasInstructions(exercise); else noCustomInstructions"
                    class="custom-instructions-content"
                  >
                    <ol class="instruction-list">
                      <li
                        *ngFor="
                          let instruction of parseCustomInstructionsToArray(
                            exercise.customInstructions
                          )
                        "
                      >
                        {{ instruction }}
                      </li>
                    </ol>
                    <div class="custom-instructions-actions">
                      <button
                        mat-button
                        color="warn"
                        (click)="deleteInstructions(exercise)"
                        class="delete-custom-instructions-button"
                        title="Delete custom instructions"
                      >
                        <mat-icon>delete</mat-icon>
                        Delete Custom Instructions
                      </button>
                    </div>
                  </div>
                  <ng-template #noCustomInstructions>
                    <p class="empty-instructions">
                      No custom instructions set for this exercise.
                    </p>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>

        <mat-card-actions align="end">
          <button
            mat-button
            color="primary"
            (click)="editInstructions(exercise)"
            class="edit-button"
          >
            <mat-icon>edit</mat-icon>
            {{
              hasInstructions(exercise)
                ? "Edit Instructions"
                : "Add Instructions"
            }}
          </button>
        </mat-card-actions>
      </mat-card>
    </div>

    <!-- Results Summary -->
    <div class="results-summary" *ngIf="filteredExercises.length > 0">
      <p>
        Showing {{ filteredExercises.length }} of
        {{ exercises.length }} exercises
        <span *ngIf="searchTerm"> matching "{{ searchTerm }}"</span>
      </p>
    </div>
  </div>
</div>
